import { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { BEARISH_BUCKETS, BULLISH_BUCKETS, statsOf } from '../logic';
import BucketCard, { GrowthPill } from './BucketCard';

/** Strongest bucket first on both sides, so +4 sits beside -4, +3 beside -3, ... on wide screens. */
export default function TrendPanel({ side, groups, live = false }) {
  const theme = useTheme();
  const buckets = side === 'bullish' ? BULLISH_BUCKETS : BEARISH_BUCKETS;
  const color = side === 'bullish' ? theme.palette.success.main : theme.palette.error.main;
  const Icon = side === 'bullish' ? TrendingUpIcon : TrendingDownIcon;
  const all = useMemo(() => buckets.flatMap((b) => groups.get(b) ?? []), [buckets, groups]);
  const stats = useMemo(() => statsOf(all), [all]);

  return (
    <Box component="section" aria-label={`${side} trends`}>
      <Stack
        direction="row" alignItems="center" spacing={1.5}
        sx={{ mb: 1.5, px: 0.5 }}
      >
        <Box
          sx={{
            width: 38, height: 38, borderRadius: 2.5, display: 'grid', placeItems: 'center',
            color, bgcolor: alpha(color, 0.14), border: `1px solid ${alpha(color, 0.3)}`,
          }}
        >
          <Icon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ lineHeight: 1.15, textTransform: 'capitalize' }}>{side}</Typography>
          <Typography variant="caption" color="text.secondary">
            {stats.count} symbols · ▲ {stats.advancers} ▼ {stats.decliners}
          </Typography>
        </Box>
        <GrowthPill value={stats.avgGrowth} />
      </Stack>

      <Stack spacing={2}>
        {buckets.map((b) => (
          <BucketCard key={b} score={b} rows={groups.get(b) ?? []} anchorId={`bucket-${b}`} live={live} />
        ))}
      </Stack>
    </Box>
  );
}
